import type { FC } from 'react';
import { useRef, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { sanitizeHref, Url } from '@wix/da-url';
import Slider from '@wix/da-ds/pkg/Slider';
import PostCompactCard from '@wix/da-shared-react/pkg/PostCompactCard';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiContextProvidedValue,
  BiData,
  ItemClickBiEvent,
  ThumbClickCommentBiEvent,
} from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';

import s from './DywPostsSlider.scss';

export interface Props {
  posts: PapiDeviation[];
  className?: string;
  viewAllHref?: string;
  minItemsToShowViewAll?: number;
  /** Username to include in post URLs. When set, links go to /watch/{username}/posts?deviationid=XXX */
  username?: string;
  /** User ID of the specific author's feed. Used for BI sectionid. */
  userId?: number;
}

const DywPostsSlider: FC<Props> = ({
  posts,
  className,
  viewAllHref,
  minItemsToShowViewAll = 9,
  username,
  userId,
}) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLDivElement>(null);
  const logBiEvent = useBiLogger();

  const getPostUrl = useCallback(
    (post: PapiDeviation) => {
      // When username is provided (specific user feed), link to /watch/{username}/posts?deviationid=XXX
      // When username is not provided (all/everyone feed), link to /watch/posts?deviationid=XXX
      return Url.browseDeviantsYouWatchLink(
        'posts',
        username,
        post.deviationId
      );
    },
    [username]
  );

  if (!posts.length) {
    return null;
  }

  const sliderItems = posts.map((post, index) => (
    <div key={post.deviationId} className={s['post-card']}>
      <a
        href={sanitizeHref(getPostUrl(post))}
        title={post.title}
        className={s['post-link']}
        onMouseDown={e => {
          if (e.button !== 0 && e.button !== 1) return;
          const target = e.target as HTMLElement;
          const closestInteractive = target.closest('a, button');
          // Prevent bi event from being logged if the user clicks on a link or button inside the post card
          if (closestInteractive && closestInteractive !== e.currentTarget) {
            return;
          }
          logBiEvent(
            BiData<ItemClickBiEvent>({
              evid: 100,
              typeid: getDeviationResourceType(post),
              itemid: post.deviationId,
              creatorid: post.author?.useridUuid,
              rank: index,
              sectionname: 'posts_strip',
              sectionid: userId,
              section_typeid: userId ? 4 : undefined,
            })
          );
        }}
      >
        <PostCompactCard
          deviation={post}
          className={s['post-compact-card']}
          showUsername
          commentBiData={BiData<ThumbClickCommentBiEvent>({
            evid: 105,
            typeid: getDeviationResourceType(post),
            itemid: post.deviationId,
            creatorid: post.author?.useridUuid ?? '',
            rank: index,
            sectionname: BiContextProvidedValue,
            sectionid: BiContextProvidedValue,
            section_typeid: BiContextProvidedValue,
          })}
        />
      </a>
    </div>
  ));

  if (viewAllHref && sliderItems.length >= minItemsToShowViewAll) {
    sliderItems.push(
      <a
        key="view-all"
        className={s['view-all-slide']}
        href={sanitizeHref(viewAllHref)}
      >
        {t('common.viewAll')}
      </a>
    );
  }

  return (
    <BiLoggerContextProvider
      value={{
        sectionname: 'posts_strip',
        sectionid: userId,
        section_typeid: userId ? 4 : undefined,
      }}
    >
      <section className={classnames(s['root'], className)}>
        <div className={s['slider-wrapper']} ref={sliderRef}>
          <Slider
            className={s['slider']}
            contentClassName={s['slider-content']}
          >
            {sliderItems}
          </Slider>
        </div>
      </section>
    </BiLoggerContextProvider>
  );
};

DywPostsSlider.displayName = 'DywPostsSlider';

export default DywPostsSlider;
